import { getPetIndex } from "@/modules/Pet/helpers/helpers"
import { Meta, Owner, Pet } from "@/shared/types/global-store"
import { Breed } from "@/shared/types/global-store"
import { PricingResponse } from "@/shared/types/lead-api-types"
import { BundledQuoteItem, CreateQuoteResponse, SyncBackBody } from "@/shared/types/lead-api-types"
import { getOrCreateUserId } from "@/shared/utils/helpers"
import { generatePetId } from "@/shared/utils/helpers"
import { assign } from "@xstate/immer"
import * as R from "ramda"
import { createMachine } from "xstate"
import { interpret } from "xstate"

// Opt-in only: set NEXT_PUBLIC_XSTATE_INSPECT=true in .env.local to enable.
// Off by default so dev sessions and Playwright don't get a stately.ai popup.
if (typeof window !== "undefined" && process.env.NEXT_PUBLIC_XSTATE_INSPECT === "true") {
  void import("@xstate/inspect").then(({ inspect }) => {
    inspect({ iframe: false })
  })
}

export type Marketing = {
  referrer_details?: string
  referrer_type?: string
  shop?: string
}

export type SubscriptionFlowContext = {
  pets: Pet[]
  currentPetId?: string
  owner: Owner
  meta: Meta
  pricing?: PricingResponse
  marketing: Marketing
  step: number
  setLoadingPrepackage: boolean
  rcContractId?: string
  prepackageVersion?: "A" | "B" | null
}

export type NewSubscriptionFlowContext = {
  quote: Partial<SyncBackBody>
  currentPetIdx: number | null
  rc_contract_id?: string
  risk_carrier?: string
  pricing_version?: string
  currentPetId: string | null
  nextPetIndex: number
  setLoadingPrepackage: boolean
  isFromLandingPage: boolean
  termsAccepted: boolean
  stripeClientSecret: string | null
  bundle_id?: string | null
  liability_quote_id?: string | null
  isEditingSinglePet: boolean
}

type AddPetNameEvent = {
  type: "ADD_PET_NAME"
  petName: string
  petId?: string | null
}

type UpdateCurrentPetIdx = {
  type: "UPDATE_CURRENT_PET_IDX"
  petIndex: number
  petId?: string | null
}

type AddPetInfosEvent = {
  type: "ADD_PET_INFORMATIONS"
  petGender?: Pet["sexe"]
  petType?: Pet["type"]
  petId: string
}

type AddPetBirthDateEvent = { type: "ADD_PET_BIRTH_DATE"; petBirthDate: string; petIndex: number }

export type AddPetBreedEvent = {
  type: "ADD_PET_BREED"
  petBreed: Partial<Breed>
  petIndex: number
}

export type SetQuoteFromBackendEvent = {
  type: "SET_QUOTE_FROM_BACKEND"
  quoteResponse: CreateQuoteResponse | BundledQuoteItem[]
}

export type AddAttributionQuestionEvent = {
  type: "ADD_ATTRIBUTION_QUESTION"
  referrer_details?: string
  referrer_type?: string
  shop?: string
}

export type AddOwnerFirstInformations = {
  type: "ADD_OWNER_FIRST_INFORMATIONS"
  email?: string
  rc_contract_id: string
  risk_carrier: string
  pricing_version: string
}

export type UpdateQuoteEvent = {
  type: "UPDATE_QUOTE"
  quote: Partial<SyncBackBody>
}

export type SetFromLandingPageEvent = {
  type: "SET_FROM_LANDING_PAGE"
}

export type SetCurrentPetIdx = {
  type: "SET_CURRENT_PET_IDX"
  petIndex: number | null
  petId: string | null
  isEditingSinglePet?: boolean
}

export type SetTermsAcceptedEvent = {
  type: "SET_TERMS_ACCEPTED"
  termsAccepted: boolean
}

export type AddNewPet = {
  type: "ADD_NEW_PET"
}

export type StopLoadingPrepackage = {
  type: "STOP_LOADING_PREPACKAGE"
}

export type StartLoadingPrepackage = {
  type: "START_LOADING_PREPACKAGE"
}

export type SetStripeClientSecretEvent = {
  type: "SET_STRIPE_CLIENT_SECRET"
  clientSecret: string | null
}

export type SetEmailSentToEvent = {
  type: "SET_EMAIL_SENT_TO"
  email: string
}

export type SetRcDataEvent = {
  type: "SET_RC_DATA"
  petIdx: number
  rcPrice?: number | null
  rcPreviouslyInsured?: boolean | null
  rcClaimsCount?: string | null
}

export type SetRcSelectedEvent = {
  type: "SET_RC_SELECTED"
  petIdx: number
  rcSelected: boolean
}

export type SetBundleDataEvent = {
  type: "SET_BUNDLE_DATA"
  bundle_id?: string | null
  liability_quote_id?: string | null
  quote_id?: string | null
  ab_test?: Meta["ab_test"]
}

export type NewSubscriptionFlowEvents =
  | AddPetNameEvent
  | AddPetInfosEvent
  | AddPetBreedEvent
  | AddPetBirthDateEvent
  | SetQuoteFromBackendEvent
  | AddAttributionQuestionEvent
  | AddOwnerFirstInformations
  | UpdateQuoteEvent
  | UpdateCurrentPetIdx
  | SetCurrentPetIdx
  | AddNewPet
  | StopLoadingPrepackage
  | StartLoadingPrepackage
  | SetFromLandingPageEvent
  | SetTermsAcceptedEvent
  | SetStripeClientSecretEvent
  | SetEmailSentToEvent
  | SetRcDataEvent
  | SetRcSelectedEvent
  | SetBundleDataEvent
export const newSubscriptionFlowMachine = createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QDswHcC0sCuAjWAxgE4CWADgC4kD2yGAZgDbVoB0JEjYAxAIIAi-APoAFAKIAVIQDleAWTEBtAAwBdRKDLVYJKrQ0gAHogCMJgKysALACYAnADY7ym7bvmzNgDQgAnogB2cwdWBwCTAA4TK2VzALsIuIBfJJ9UTBx8YnI9OiYWdk4eAWFxKQBJaQAxAHkAJTleCXKa6QBlFXUkEC0dXINjBAjlVhtzAGZnJ3M7II8TH38EBwcRm0inIICA5QCrcxS09Cw8QlJKGjzmNg4uPkFRSSEAITqxMX5Og17dS4HTEzjawmZQWOxWBwmBxWCJ2caLRDBIEBBzmcxRZSOKHjA6pEDpE5Zc65BjXQp3EqPKTPcp1CQACSE-CaSjU320v303UGdhsrEiON5GNm6zsCIQEICrHGJlc0Mh5jGrkO+OOmTOOUupIKtx4bSeAEUAKo1CRiIRVOo1OQvXgAYQA0mJpJ82d0fv1uYhUaEwiKceNwg5xt4-IhAyNEnCYYCrOMrICVQT1dkLrRtTcivdhE0JHVys8jc1WkJjWI2sXpF93RzPaBBg4+SYthErHCAnKUeKbHtrMpGzLUfHxi4Akm1adUyT8pmKQ8agB1aRiOoW2kVoSVWoNJotdrVzS1v5eiWhpbNqXuGUWZtjEy88bjjKT4lamfknhGkTMs2lk1mg8eiPLl6wBSxxkFHE9nRSEz0QKJrDsJCEicKFlAiGwUjxZBqAgOADGTF9NXTGd2T6Y9QIQDAHHFDBLGQhjGKQx9sInIliKuHUijIzlkH+BAoRMVhwQmDZFXGWEAnFRIIn5Vw4ViCTYTCLCkiAA */
  tsTypes: {} as import("./machine.typegen").Typegen0,
  predictableActionArguments: true,
  schema: {
    context: {} as NewSubscriptionFlowContext,
    events: {} as NewSubscriptionFlowEvents,
  },
  id: "new-subscription-flow",
  context: {
    quote: {
      pets: [],
      meta: {
        user_id: getOrCreateUserId(),
      },
    },
    currentPetIdx: 0,
    currentPetId: null,
    nextPetIndex: 0,
    setLoadingPrepackage: false,
    isFromLandingPage: false,
    termsAccepted: false,
    stripeClientSecret: null,
    isEditingSinglePet: false,
  },
  initial: "idle",
  states: {
    idle: {
      on: {
        ADD_PET_NAME: {
          actions: assign<NewSubscriptionFlowContext, AddPetNameEvent>(
            (context, { petName, petId }) => {
              const petIndexToEdit = getPetIndex(context.quote.pets || [], petId)
              if (context.quote?.pets?.[petIndexToEdit]) {
                ;(context.quote.pets[petIndexToEdit] || {}).name = petName
              } else {
                const newPetId = context.currentPetId || generatePetId()
                context.quote.pets = [
                  ...(context.quote.pets || []),
                  {
                    name: petName,
                    idx: context.nextPetIndex,
                    id: newPetId,
                  },
                ]
                context.currentPetId = newPetId
              }
            }
          ),
        },
        UPDATE_CURRENT_PET_IDX: {
          actions: assign<NewSubscriptionFlowContext, UpdateCurrentPetIdx>(
            (context, { petIndex, petId }) => {
              if (petId) {
                context.currentPetId = petId
              }
              context.currentPetIdx = petIndex
            }
          ),
        },
        ADD_PET_INFORMATIONS: {
          actions: assign<NewSubscriptionFlowContext, AddPetInfosEvent>(
            (context, { petGender, petType, petId }) => {
              const petIndexToEdit = getPetIndex(context.quote.pets || [], petId)
              if (context.quote && context.quote.pets && context.quote.pets[petIndexToEdit]) {
                context.quote.pets[petIndexToEdit].sexe = petGender
                context.quote.pets[petIndexToEdit].type = petType
              }
            }
          ),
        },
        ADD_PET_BREED: {
          actions: assign<NewSubscriptionFlowContext, AddPetBreedEvent>(
            (context, { petBreed, petIndex }) => {
              if (context.quote.pets?.[petIndex]) {
                context.quote.pets[petIndex].breed = petBreed
              }
            }
          ),
        },
        ADD_PET_BIRTH_DATE: {
          actions: assign<NewSubscriptionFlowContext, AddPetBirthDateEvent>(
            (context, { petBirthDate, petIndex }) => {
              if (context.quote.pets?.[petIndex]) {
                context.quote.pets[petIndex].birthday = petBirthDate
              }
            }
          ),
        },
        SET_QUOTE_FROM_BACKEND: {
          actions: assign<NewSubscriptionFlowContext, SetQuoteFromBackendEvent>(
            (context, { quoteResponse }) => {
              if (quoteResponse[0]?.quote_id) {
                context.quote.meta = {
                  ...(context.quote?.meta || {}),
                  quote_id: quoteResponse[0]?.quote_id,
                  start_date: quoteResponse[0].start_date,
                  subscription_mode: quoteResponse[0].subscription_mode,
                  // AB assignments are made at creation (DLM-1889); keep the
                  // known ones when a later response doesn't carry them.
                  ab_test: quoteResponse[0].ab_test ?? context.quote?.meta?.ab_test,
                }
              }
              if (R.isNotNil(context.currentPetId)) {
                const petIndexToEdit = getPetIndex(context.quote.pets || [], context.currentPetId)
                if (context.quote?.pets?.[petIndexToEdit] && quoteResponse[0]?.created_at) {
                  context.quote.pets[petIndexToEdit].created_at = quoteResponse[0]?.created_at
                }
              }
            }
          ),
        },
        ADD_ATTRIBUTION_QUESTION: {
          actions: assign<NewSubscriptionFlowContext, AddAttributionQuestionEvent>(
            (context, event) => {
              context.quote.marketing = {
                ...(context.quote.marketing || {}),
                referrer_details: event.referrer_details,
                referrer_type: event.referrer_type,
                shop: event.shop || undefined,
              }
            }
          ),
        },
        ADD_OWNER_FIRST_INFORMATIONS: {
          actions: assign<NewSubscriptionFlowContext, AddOwnerFirstInformations>(
            (context, event) => {
              if (event.email) {
                context.quote.owner = {
                  ...(context.quote.owner || {}),
                  email: event.email,
                }
              }
              context.pricing_version = event.pricing_version
              context.rc_contract_id = event.rc_contract_id
              context.risk_carrier = event.risk_carrier
            }
          ),
        },
        UPDATE_QUOTE: {
          actions: assign<NewSubscriptionFlowContext, UpdateQuoteEvent>((context, event) => {
            context.quote = event.quote
            context.pricing_version = event.quote.meta?.pricing_version ?? context.pricing_version
          }),
        },
        SET_FROM_LANDING_PAGE: {
          actions: assign<NewSubscriptionFlowContext, SetFromLandingPageEvent>((context) => {
            context.isFromLandingPage = true
          }),
        },
        SET_CURRENT_PET_IDX: {
          actions: assign<NewSubscriptionFlowContext, SetCurrentPetIdx>((context, event) => {
            context.currentPetIdx = event.petIndex
            context.currentPetId = event.petId
            context.isEditingSinglePet = event.isEditingSinglePet ?? false
          }),
        },
        SET_TERMS_ACCEPTED: {
          actions: assign<NewSubscriptionFlowContext, SetTermsAcceptedEvent>((context, event) => {
            context.termsAccepted = event.termsAccepted
          }),
        },
        ADD_NEW_PET: {
          actions: assign<NewSubscriptionFlowContext, AddNewPet>((context) => {
            const petIndexes = R.map((pet) => pet.idx, context.quote.pets || [])
            // @ts-expect-error this is an expected error as id isn't in backend type
            const maxIndex: number = R.reduce(R.maxBy(R.identity), 0, petIndexes)
            const computedMaxIndex = R.isNotNil(maxIndex) ? maxIndex : -1
            context.currentPetIdx = computedMaxIndex + 1
            context.nextPetIndex = computedMaxIndex + 1
            context.currentPetId = null
            context.isEditingSinglePet = false
          }),
        },
        STOP_LOADING_PREPACKAGE: {
          actions: assign<NewSubscriptionFlowContext, StopLoadingPrepackage>((context) => {
            context.setLoadingPrepackage = false
          }),
        },
        START_LOADING_PREPACKAGE: {
          actions: assign<NewSubscriptionFlowContext, StartLoadingPrepackage>((context) => {
            context.setLoadingPrepackage = true
          }),
        },
        SET_STRIPE_CLIENT_SECRET: {
          actions: assign<NewSubscriptionFlowContext, SetStripeClientSecretEvent>(
            (context, event) => {
              context.stripeClientSecret = event.clientSecret
            }
          ),
        },
        SET_EMAIL_SENT_TO: {
          actions: assign<NewSubscriptionFlowContext, SetEmailSentToEvent>((context, event) => {
            context.quote.owner = { ...(context.quote.owner || {}), email: event.email }
          }),
        },
        SET_RC_DATA: {
          actions: assign<NewSubscriptionFlowContext, SetRcDataEvent>((context, event) => {
            const pet = (context.quote.pets || []).find((p) => p.idx === event.petIdx)
            if (!pet) return
            pet.rc_price = event.rcPrice
            pet.has_previous_insurance = event.rcPreviouslyInsured ?? undefined
            pet.number_of_previous_claims =
              event.rcClaimsCount != null
                ? (Number(event.rcClaimsCount) as 0 | 1 | 2 | 3)
                : undefined
          }),
        },
        SET_RC_SELECTED: {
          actions: assign<NewSubscriptionFlowContext, SetRcSelectedEvent>((context, event) => {
            const pet = (context.quote.pets || []).find((p) => p.idx === event.petIdx)
            if (!pet) return
            pet.liability = event.rcSelected
          }),
        },
        SET_BUNDLE_DATA: {
          actions: assign<NewSubscriptionFlowContext, SetBundleDataEvent>((context, event) => {
            if (event.bundle_id !== undefined) context.bundle_id = event.bundle_id
            if (event.liability_quote_id !== undefined)
              context.liability_quote_id = event.liability_quote_id
            // The first `PUT /bundled-quote` from an LP-created (partner) lead
            // mints a NEW health quote_id (create path). Adopt it as canonical so
            // subsequent GETs stop reading the orphaned partner quote.
            if (event.quote_id) {
              context.quote.meta = {
                ...(context.quote.meta || {}),
                quote_id: event.quote_id,
              }
            }
            // The create path of `PUT /bundled-quote` is where an LP-created
            // lead gets its AB assignments (DLM-1889).
            if (event.ab_test) {
              context.quote.meta = {
                ...(context.quote.meta || {}),
                ab_test: event.ab_test,
              }
            }
          }),
        },
      },
    },
  },
})

export const newSubscriptionFlowService = interpret(newSubscriptionFlowMachine, {
  devTools: true,
}).start()
